import React from "react";
import { Loader2 } from "lucide-react";
import type { InsightDto } from "../../types/weekly-cost-report.types";
import { formatWeeklyCostInsight } from "../../utils/weekly-cost-report/weeklyCostInsightFormatter.utils";

const InsightBox = ({
  insight,
  customerName,
  index,
}: {
  insight: InsightDto;
  customerName?: string | null;
  index: number;
}) => {
  const formattedInsight = formatWeeklyCostInsight(insight, {
    customerName,
    index,
  });
  const showShare = Boolean(formattedInsight.shareText);
  const showTrend = Boolean(formattedInsight.trendText);

  return (
    <div
      data-testid="weekly-insight-row"
      className="flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm"
      style={{
        borderTop: "1px solid var(--border-subtle)",
        borderRight: "1px solid var(--border-subtle)",
        borderBottom: "1px solid var(--border-subtle)",
        borderLeft: `4px solid ${formattedInsight.borderColor}`,
      }}
    >
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold"
        style={{
          borderColor: formattedInsight.borderColor,
          color: formattedInsight.borderColor,
        }}
      >
        <i
          className={formattedInsight.iconClassName}
          style={{ fontSize: "14px" }}
        ></i>
      </div>

      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <span className="shrink-0 font-semibold text-[var(--text-primary)]">
          {formattedInsight.label}:
        </span>

        {formattedInsight.entityName && (
          <>
            <span className="shrink-0 font-semibold uppercase text-[var(--wcr-insight-entity-color)]">
              {formattedInsight.entityName}
            </span>
            <span className="shrink-0 text-[var(--text-tertiary)]">|</span>
          </>
        )}

        <span className="shrink-0 text-[var(--text-secondary)]">Spend:</span>
        <span className="shrink-0 font-bold text-[var(--aqua)]">
          {formattedInsight.spendText}
        </span>

        {showShare && (
          <>
            <span className="shrink-0 text-[var(--text-tertiary)]">|</span>
            <span className="shrink-0 text-[var(--text-secondary)]">
              Share: 
            </span>
            <span className="shrink-0 font-bold text-[var(--aqua)]">
              {formattedInsight.shareText}
            </span>
          </>
        )}

        {showTrend && (
          <>
            <span className="shrink-0 text-[var(--text-tertiary)]">|</span>
            <span
              className={`flex shrink-0 items-center gap-1 font-semibold ${formattedInsight.trendClassName}`}
            >
              {formattedInsight.trendIconClassName && (
                <i
                  className={formattedInsight.trendIconClassName}
                  style={{ fontSize: "12px" }}
                ></i>
              )}
              {formattedInsight.trendText}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

interface WeeklyCostInsightPanelProps {
  insights: InsightDto[];
  customerName?: string | null;

  isLoadingCustomerInsights?: boolean;
  isPdfMode?: boolean;
}

const WeeklyCostInsightPanel = React.memo(({
  insights,
  customerName,

  isLoadingCustomerInsights = false,
  isPdfMode = false,
}: WeeklyCostInsightPanelProps) => (
  <div className={isPdfMode ? "flex flex-col h-auto overflow-visible" : "flex flex-col h-full overflow-y-auto"}>
    {isLoadingCustomerInsights ? (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 size={24} className="animate-spin text-[var(--text-secondary)]" />
          <span className="text-xs text-[var(--text-tertiary)]">Loading insights...</span>
        </div>
      </div>
    ) : (
      <div className="flex flex-col gap-2 px-2">
        {insights && insights.length > 0 ? (
          insights.map((insight, index) => (
            <InsightBox
              key={`${insight.label}-${index}`}
              insight={insight}
              customerName={customerName}
              index={index}
            />
          ))
        ) : (
          <div className="flex h-full items-center justify-center text-center text-xs text-[var(--text-tertiary)] py-4">
            No insights available
          </div>
        )}
      </div>
    )}
  </div>
));



export default WeeklyCostInsightPanel;
